import React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

interface SortableGroupRowProps {
  id: string
  title: string
  /** Whether this group currently delegates to a fixed KPI slug (vs. a custom group). */
  isBuiltin: boolean
  metricCount?: number
  onEdit?: () => void
  onRemove: () => void
}

// A group row in the report structure editor: drag handle to reorder, a kind
// badge, an Edit button, and a remove button. Editing a builtin group converts
// it into an equivalent custom group (seeded with that slug's current metrics)
// so its metrics can be customized.
const SortableGroupRow: React.FC<SortableGroupRowProps> = ({
  id,
  title,
  isBuiltin,
  metricCount,
  onEdit,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center justify-between gap-3 p-4 bg-system-blue-800 border border-system-blue-600 rounded-system-md",
        isDragging && "opacity-60 shadow-xl z-50 border-system-blue-500",
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-6 h-6 bg-system-blue-600 rounded-system-xs cursor-grab active:cursor-grabbing flex-shrink-0"
          title="Drag to reorder"
        >
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </button>
        <div className="min-w-0">
          <p className="text-white font-medium truncate">{title}</p>
          <p className="text-system-blue-300 text-xs">
            {isBuiltin
              ? "Built-in"
              : `Custom · ${metricCount ?? 0} metric${metricCount === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="px-3 py-1.5 text-xs text-white bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
          >
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="text-system-blue-300 hover:text-system-error-400 transition-colors"
          title="Remove group"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default SortableGroupRow
